import React, { useState, useEffect } from 'react';
import { Joyride, STATUS, ACTIONS } from 'react-joyride';

function scrollSidebarTo(targetId) {
    return () => {
        const target = document.querySelector(targetId);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        return new Promise(resolve => setTimeout(resolve, 400));
    };
}

const tourSteps = [
    {
        target: '#tourTooltip',
        content: 'Is this your first time here? Please take a minute to see the most important features. You can always restart the tour here.',
        placement: 'bottom',
    },
    {
        target: '#myColumnGroups',
        content: 'Select Displayed columns and save views here.',
        placement: 'right',
        skipScroll: true,
        before: scrollSidebarTo('#myColumnGroups'),
    },
    {
        target: '#HighlightText',
        content: 'Highlight text by typing here.',
        placement: 'right',
        skipScroll: true,
        before: scrollSidebarTo('#HighlightText'),
    },
    {
        target: '#FilterText',
        content: 'Filter results by typing text here. For example type the word claim.',
        placement: 'right',
        skipScroll: true,
        before: scrollSidebarTo('#FilterText'),
    },
    {
        target: '#annotationFilter',
        content: 'Filter results by selecting checkboxes/stars/colors using Annotation filters here.',
        placement: 'right',
        skipScroll: true,
        before: scrollSidebarTo('#annotationFilter'),
    },
    {
        target: '#advFilter',
        content: 'Custom result filters.',
        placement: 'top',
        skipScroll: true,
        before: scrollSidebarTo('#advFilter'),
    },
    {
        target: '#GQPowerTools',
        content: 'Variation Filters and Landscape Reports for subscribing users.',
        placement: 'right',
        skipScroll: true,
        before: scrollSidebarTo('#GQPowerTools'),
    },
    {
        target: '#applyButtonBlock',
        content: 'Always press the Apply button after setting up a filter.',
        placement: 'top',
    },
    {
        target: '#ExportResults',
        content: 'Export Results Here.',
        placement: 'right',
        skipScroll: true,
        before: scrollSidebarTo('#ExportResults'),
    },
    {
        target: '#sortTooltip',
        content: 'Sort results.',
        placement: 'bottom',
    },
    {
        target: '#viewTooltip',
        content: 'Group results by Patent Family, Publication Number, etc.',
        placement: 'bottom',
    },
];

export default function Tour({
    handleOnClick,
    tourStart,
    tourFlag,
    handleTourFlag
}) {
    const [tourKey] = useState(() => Date.now());

    useEffect(() => {
        if (tourStart && tourFlag) {
            handleTourFlag();
        }
    }, []);

    // Close the tour on ESC. joyride's dismissKeyAction can't trigger skip and its
    // close() advances instead of ending, so end the tour ourselves.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleOnClick();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleOnClick]);

    // Lock user scrolling while the tour is active. Each step positions its target via
    // the `before` hook (scrollSidebarTo), so if the user scrolls, the target moves and
    // the highlight/tooltip drift (the spotlight snaps to the new position on scroll end).
    // Block user scroll gestures only; programmatic scrollIntoView still works.
    useEffect(() => {
        const preventScroll = (event) => {
            event.preventDefault();
        };
        const scrollKeys = [' ', 'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        const preventScrollKeys = (event) => {
            // Don't hijack keys on interactive elements (typing in inputs, or
            // activating a focused button/link with Space/Enter).
            const target = event.target;
            const tag = target && target.tagName;
            if (
                tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' ||
                target?.isContentEditable || target?.getAttribute?.('role') === 'button'
            ) {
                return;
            }
            if (scrollKeys.includes(event.key)) {
                event.preventDefault();
            }
        };
        window.addEventListener('wheel', preventScroll, { passive: false });
        window.addEventListener('touchmove', preventScroll, { passive: false });
        window.addEventListener('keydown', preventScrollKeys, { passive: false });
        return () => {
            window.removeEventListener('wheel', preventScroll, { passive: false });
            window.removeEventListener('touchmove', preventScroll, { passive: false });
            window.removeEventListener('keydown', preventScrollKeys, { passive: false });
        };
    }, []);

    const handleJoyrideCallback = (data) => {
        const { status, action } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
            handleOnClick();
        }
    };

    return (
        <Joyride
            key={tourKey}
            steps={tourSteps}
            run={true}
            continuous={true}
            scrollToFirstStep={true}
            callback={handleJoyrideCallback}
            options={{
                primaryColor: '#ffffff',
                textColor: '#ffffff',
                backgroundColor: '#337ab7',
                arrowColor: '#337ab7',
                overlayColor: 'rgba(0, 0, 0, 0.4)',
                zIndex: 1002,
                skipBeacon: true,
                showProgress: true,
                spotlightPadding: 8,
                spotlightRadius: 8,
                width: 340,
                buttons: ['back', 'primary', 'skip', 'close'],
                overlayClickAction: null,
                // joyride's built-in close() advances to the next step (it relies on the
                // consumer to stop the tour via `run`). Route the "X" through skip instead,
                // which is the same path the working "End Tour" button uses and reliably
                // ends the tour (status: SKIPPED -> handleJoyrideCallback -> handleOnClick).
                closeButtonAction: 'skip',
                // ESC is handled manually below; dismissKeyAction only supports
                // next/replay/close, and close has the advance-instead-of-end bug.
                dismissKeyAction: null,
            }}
            locale={{
                back: '« Prev',
                close: 'Close',
                last: 'Finish',
                next: 'Next »',
                skip: 'End Tour',
                nextWithProgress: 'Next ({current} of {total}) »',
            }}
            styles={{
                // All three nav buttons share the same look: solid white background
                // with blue text (matching the primary "Next" button).
                buttonPrimary: {
                    backgroundColor: '#ffffff',
                    color: '#337ab7',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 12px',
                    fontSize: 14,
                    cursor: 'pointer',
                },
                buttonBack: {
                    backgroundColor: '#ffffff',
                    color: '#337ab7',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 12px',
                    fontSize: 14,
                    cursor: 'pointer',
                },
                buttonSkip: {
                    backgroundColor: '#ffffff',
                    color: '#337ab7',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 12px',
                    fontSize: 14,
                    cursor: 'pointer',
                },
                // Smaller "X" close symbol.
                buttonClose: {
                    color: '#ffffff',
                    height: 8,
                    width: 8,
                    padding: 8,
                },
            }}
        />
    );
}
